import {
  Alert,
  Asset,
  Button,
  Modal,
  ModalError,
  ModalLoading,
  PlusIcon,
} from '@clearcover/atlas';
import {
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyPaymentMethodsResponseType,
  MakePaymentRequestType,
  MakePaymentResponseType,
  PaymentAmountInfoType,
  PaymentMethodType,
} from '@clearcover/core-app-types';
import { AxiosPromise } from 'axios';
import currency from 'currency.js';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { enabledSweepPaymentPolicy } from 'Admin/authPolicies';
import { useAuth0 } from 'Auth';
import { PolicyContext } from 'Policies/utils';
import {
  ConfirmationModal,
  FormInput,
  FormModalContent,
  FormModalFooter,
  FormModalHeader,
} from 'Shared/components';
import { Label } from 'Shared/components/FormInput/Variants';
import { useToast } from 'Shared/components/Toast';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { AddPaymentMethodForm } from '../AddPaymentMethodForm';
import { PolicyPaymentMethod } from '../PolicyPaymentMethod';
import { ItemizedPaymentAmounts } from './ItemizedPayment';
import styles from './styles.module.css';

const PaymentOptions: FC<{
  billingData: MakePaymentResponseType;
  paymentMethodType: 'bank' | 'creditCard';
  convenienceFee: currency;
}> = ({ billingData, paymentMethodType, convenienceFee }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue, values } =
    useFormikContext<MakeOneTimePaymentFormValueType>();

  const setPaymentAmountOther = useCallback(
    (rawAmount: string) => {
      const otherAmount = billingData.paymentFeeEnabledState
        ? currency(rawAmount).add(
            billingData.paymentTypeFees[paymentMethodType]
          )
        : currency(rawAmount);

      setFieldValue(
        'paymentAmount',
        convenienceFee.add(otherAmount).toString()
      );
      setFieldValue('otherAmount', rawAmount);
    },
    [billingData, paymentMethodType, setFieldValue, convenienceFee]
  );

  const optionAmount = useCallback(
    function optionAmount(paymentAmountInfo: PaymentAmountInfoType) {
      const rawAmount = billingData.paymentFeeEnabledState
        ? currency(
            paymentMethodType === 'bank'
              ? paymentAmountInfo.minimumPaymentUsingBank
              : paymentAmountInfo.minimumPaymentUsingCreditCard
          )
        : currency(paymentAmountInfo.remainingBalance);
      return rawAmount.value === 0 ? rawAmount : rawAmount.add(convenienceFee);
    },
    [billingData.paymentFeeEnabledState, convenienceFee, paymentMethodType]
  );
  const overduePayment = useMemo(
    () => optionAmount(billingData.overdue),
    [optionAmount, billingData.overdue]
  );
  const minimumPayment = useMemo(
    () => optionAmount(billingData.minimum),
    [optionAmount, billingData.minimum]
  );
  const remainingPayment = useMemo(
    () => optionAmount(billingData.remaining),
    [optionAmount, billingData.remaining]
  );

  // One-time initialization to ensure added fees are included in default payment amount
  useEffect(() => {
    setFieldValue('paymentAmount', overduePayment.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.paymentOptions}>
      <div
        className={styles.paymentOption}
        data-testid="payment-amount-overdue"
      >
        <FormInput
          name="overdue-payment"
          value={'overdue'}
          checked={values.paymentOption === 'overdue'}
          variant="radio"
          label="Amount overdue"
          onChecked={() => {
            setFieldValue('paymentOption', 'overdue');
            setFieldValue('paymentAmount', overduePayment.toString());
          }}
        />
        <div data-testid="radio-overdue-payment-value" className={styles.value}>
          {overduePayment.format(true)}
        </div>
      </div>

      <div
        className={styles.paymentOption}
        data-testid="payment-amount-minimum"
      >
        <FormInput
          name="minimum-payment"
          value={'minimum'}
          checked={values.paymentOption === 'minimum'}
          variant="radio"
          label="Total payment due"
          onChecked={() => {
            setFieldValue('paymentOption', 'minimum');
            setFieldValue('paymentAmount', minimumPayment.toString());
          }}
        />
        <div data-testid="radio-minimum-payment-value" className={styles.value}>
          {minimumPayment.format(true)}
        </div>
      </div>

      <div
        className={styles.paymentOption}
        data-testid="payment-amount-remaining"
      >
        <FormInput
          name="remaining-payment"
          value={'remaining'}
          checked={values.paymentOption === 'remaining'}
          variant="radio"
          label="Total balance"
          onChecked={() => {
            setFieldValue('paymentOption', 'remaining');
            setFieldValue('paymentAmount', remainingPayment.toString());
          }}
        />
        <div
          data-testid="radio-remaining-payment-value"
          className={styles.value}
        >
          {remainingPayment.format(true)}
        </div>
      </div>

      <div className={styles.paymentOption} data-testid="payment-amount-other">
        <FormInput
          name="payment-amount-other"
          value={'other'}
          checked={values.paymentOption === 'other'}
          variant="radio"
          label="Other amount"
          onChecked={() => {
            inputRef?.current?.focus();
            setFieldValue('paymentOption', 'other');
            setPaymentAmountOther(values.otherAmount);
          }}
        />
        <div className={styles.value}>
          <input
            type="text"
            value={values.otherAmount}
            ref={inputRef}
            onChange={(e) => {
              if (values.paymentOption !== 'other')
                setFieldValue('paymentOption', 'other');
              setPaymentAmountOther(e.currentTarget.value);
            }}
            data-testid="payment-amount-other-input"
            className={styles.otherAmountInput}
          />
        </div>
      </div>
    </div>
  );
};

function findPaymentMethod({
  paymentMethods,
  paymentMethodId,
}: {
  paymentMethods: Array<PaymentMethodType>;
  paymentMethodId: string;
}): PaymentMethodType {
  const paymentMethod = paymentMethods.find((pm) => pm.id === paymentMethodId);
  if (!paymentMethod) {
    throw Error('Payment method not found');
  }
  return paymentMethod;
}

export interface MakeOneTimePaymentFormProps {
  close: () => void;
  fetchBillingData?: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
}

const MAKE_ONE_TIME_PAYMENT_SCHEMA = Yup.object({
  paymentOption: Yup.string().required('Required'),
  paymentMethodId: Yup.string().required('Required'),
  paymentAmount: Yup.number().required('Required'),
});

export type MakeOneTimePaymentFormValueType = {
  paymentOption: 'overdue' | 'minimum' | 'remaining' | 'other';
  paymentMethodId: string;
  paymentAmount: string;
  otherAmount: string;
};

export const MakeOneTimePaymentForm: FC<MakeOneTimePaymentFormProps> = ({
  close,
  fetchBillingData,
}) => {
  const { toast } = useToast();
  const { id: policyId } = useParams<{
    id: string;
  }>();

  const {
    refetchHandler,
    details: { region, termId },
    resourceEffectiveGrants,
  } = useContext(PolicyContext);

  const [{ data, loading: optionsLoading, error: optionsError }] =
    useApi<MakePaymentResponseType>(
      `payments/${policyId}/options?region=${region}&termId=${termId}`
    );

  const [
    {
      data: policyPaymentMethods,
      loading: paymentMethodsLoading,
      error: paymentMethodsError,
      refetch: refetchPolicyPaymentMethods,
    },
  ] = useApi<GetPolicyPaymentMethodsResponseType>(
    `/policies/${policyId}/paymentMethods`
  );
  const { user: currentUser } = useAuth0();

  const sweepPaymentEnabled = enabledSweepPaymentPolicy(
    resourceEffectiveGrants
  );

  const convenienceFee = useMemo(
    () =>
      currency(
        currentUser?.isInternalUser
          ? data?.paymentTypeFees?.convenienceFee
          : '0'
      ),
    [data?.paymentTypeFees?.convenienceFee, currentUser?.isInternalUser]
  );

  const visiblePaymentMethods = useMemo(() => {
    if (!policyPaymentMethods?.paymentMethods) {
      return [];
    }
    return sweepPaymentEnabled
      ? policyPaymentMethods.paymentMethods
      : policyPaymentMethods.paymentMethods.filter(
          (pm) => pm.paymentProcessor !== 'SWEEP'
        );
  }, [policyPaymentMethods, sweepPaymentEnabled]);

  const handleAfterAddPaymentMethod = useCallback(async () => {
    await fetchBillingData?.();
    if (refetchPolicyPaymentMethods) {
      await refetchPolicyPaymentMethods();
    }
  }, [fetchBillingData, refetchPolicyPaymentMethods]);

  const initialPaymentMethodId = useMemo(() => {
    if (!policyPaymentMethods?.defaultPaymentMethod) {
      return '';
    }
    const defaultId = policyPaymentMethods.defaultPaymentMethod.id;
    if (visiblePaymentMethods.some((pm) => pm.id === defaultId)) {
      return defaultId;
    }
    return visiblePaymentMethods[0]?.id ?? defaultId;
  }, [policyPaymentMethods, visiblePaymentMethods]);

  const [, makeOneTimePayment] = useLazyApi<MakePaymentRequestType>({
    method: 'POST',
    url: `/policies/${policyId}/makePayment`,
  });

  const handleFormSubmit = useCallback(
    (
      values: MakeOneTimePaymentFormValueType,
      {
        setStatus,
        setSubmitting,
      }: FormikHelpers<MakeOneTimePaymentFormValueType>
    ) => {
      return makeOneTimePayment({
        data: {
          paymentMethodId: values.paymentMethodId,
          paymentTotal: parseFloat(values.paymentAmount),
          termId,
          chargeConvenienceFee: convenienceFee.value !== 0,
        },
      })
        .then(() => {
          toast({
            message: 'Payment made successfully.',
            type: 'success',
            position: 'bottom',
            withSecondaryNav: false,
          });

          refetchHandler();
          close();
        })
        .catch((err) => {
          toast({
            message: err.response.data.message ?? err.message,
            type: 'error',
            position: 'top',
            withSecondaryNav: false,
            width: 'full',
            icon: 'info',
          });
          setSubmitting(false);
          setStatus(err.response.data.message ?? err.message);
        });
    },
    [
      makeOneTimePayment,
      convenienceFee.value,
      termId,
      toast,
      refetchHandler,
      close,
    ]
  );

  if (optionsLoading || paymentMethodsLoading) {
    return <ModalLoading data-testid="otpayment-loading" />;
  }

  const initializationError = optionsError || paymentMethodsError;

  if (!!initializationError) {
    return (
      <ModalError
        data-testid="otpayment-error"
        errorMessage={initializationError.message}
        componentStack={initializationError.stack}
      />
    );
  }

  const initialFormValues: MakeOneTimePaymentFormValueType = {
    paymentOption: 'overdue',
    paymentMethodId:
      visiblePaymentMethods.length === 0 ? '' : initialPaymentMethodId,
    paymentAmount: data.overdue.minimumPayment,
    otherAmount: '0',
  };

  return (
    <Formik
      initialValues={initialFormValues}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validationSchema={MAKE_ONE_TIME_PAYMENT_SCHEMA}
      validateOnMount
    >
      {({ setFieldValue, isValid, values, isSubmitting, handleSubmit }) => {
        const hasNoVisiblePaymentMethods =
          policyPaymentMethods != null && visiblePaymentMethods.length === 0;

        const totalAmount = currency(values.paymentAmount, {
          formatWithSymbol: true,
        });

        const paymentMethod = hasNoVisiblePaymentMethods
          ? ({ type: 'creditCard' as const } as PaymentMethodType)
          : findPaymentMethod({
              paymentMethods: visiblePaymentMethods,
              paymentMethodId: values.paymentMethodId,
            });

        const chosenSuggestion =
          values.paymentOption === 'other'
            ? {
                productAmount: '0',
                installmentFee: '0',
                productFees: '0',
                waivedFees: '0',
                remainingBalance: '0',
                memberSurplusContribution: '0',
                taxesTotal: '0',
              }
            : data[values.paymentOption];
        const noPaymentDue =
          totalAmount.value === 0 ||
          (values.paymentOption === 'other' &&
            currency(values.otherAmount).value === 0);

        const cannotContinuePayment =
          isSubmitting || noPaymentDue || hasNoVisiblePaymentMethods;

        return (
          <>
            <FormModalHeader>Make a Payment</FormModalHeader>
            <FormModalContent>
              <div className={styles.section}>
                <h1>Make a payment</h1>
                {hasNoVisiblePaymentMethods ? (
                  <div className={styles.noPaymentMethods}>
                    <Alert
                      type="warning"
                      data-testid="ot-payment-no-methods-warning"
                      message="Add a payment method, then return here to pay"
                    />
                    <p
                      className={styles.noPaymentMethodsSubtext}
                      data-testid="ot-payment-no-methods-subtext"
                    >
                      No personal payment methods on file.
                    </p>
                    {fetchBillingData && (
                      <div className={styles.addPaymentMethodToggle}>
                        <Modal
                          variant="full"
                          data-testid="ot-payment-add-method-modal"
                          toggle={(open) => (
                            <Button
                              variant="secondary"
                              onClick={open}
                              data-testid="ot-payment-add-method"
                            >
                              <Asset
                                icon={PlusIcon}
                                description="Add payment method"
                                height={12}
                                width={12}
                                className={styles.addPaymentMethodIcon}
                              />
                              Add new payment method
                            </Button>
                          )}
                        >
                          {(addClose) => (
                            <AddPaymentMethodForm
                              close={addClose}
                              isProcessOne={false}
                              hasAdyenPaymentMethod={policyPaymentMethods.paymentMethods.some(
                                (m) => m.paymentProcessor === 'ADYEN'
                              )}
                              fetchBillingData={handleAfterAddPaymentMethod}
                            />
                          )}
                        </Modal>
                      </div>
                    )}
                  </div>
                ) : (
                  <PolicyPaymentMethod
                    policyPaymentMethods={visiblePaymentMethods}
                    value={values.paymentMethodId}
                    setValue={(value) =>
                      setFieldValue('paymentMethodId', value)
                    }
                  />
                )}
              </div>
              <div className={styles.section}>
                <Label>Select payment amount</Label>
                <PaymentOptions
                  billingData={data}
                  paymentMethodType={paymentMethod.type}
                  convenienceFee={convenienceFee}
                />

                <ItemizedPaymentAmounts
                  chosenPaymentMethodType={paymentMethod.type}
                  paymentFeeEnabledState={data.paymentFeeEnabledState}
                  paymentTypeFees={data.paymentTypeFees}
                  itemizedAmounts={chosenSuggestion}
                  totalAmount={totalAmount}
                />
              </div>
            </FormModalContent>
            <FormModalFooter
              showStatus={false}
              close={close}
              checkDirty={false}
              identifier={
                hasNoVisiblePaymentMethods
                  ? 'ot-payment-empty'
                  : `${values.paymentMethodId}-make-payment`
              }
              confirmationModal={
                <Modal
                  variant="normal"
                  toggle={(openModal) => (
                    <Button
                      data-testid="one-time-payment-exit"
                      size={'md'}
                      variant="primary"
                      type="button"
                      onClick={openModal}
                      disabled={cannotContinuePayment}
                    >
                      Continue
                    </Button>
                  )}
                >
                  {(closeConfirm) => (
                    <ConfirmationModal
                      onSubmit={() => handleSubmit()}
                      close={closeConfirm}
                      disabled={!isValid || hasNoVisiblePaymentMethods}
                      isSubmitting={isSubmitting}
                      variant="primary"
                      headerText="Confirm one time payment"
                      bodyText={`This will be a one time payment of ${currency(
                        values.paymentAmount,
                        {
                          formatWithSymbol: true,
                        }
                      ).format()}. Please confirm.`}
                    />
                  )}
                </Modal>
              }
            />
          </>
        );
      }}
    </Formik>
  );
};

MakeOneTimePaymentForm.displayName = 'MakeOneTimePaymentForm';
